import { Box, Grid, Tab, Typography } from '@mui/material';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/** TRANSLATION */
import { useTranslation } from 'react-i18next';

/** ROUTE */
import { routeName } from 'routes/routes-name';

/** API */
import { fileUrl } from 'api/api';
import InventoryApi from 'api/warehouses/inventory.api';

/** COMPONENT */
import HeaderLabel from 'component/CardCustom/HeaderLabel';
import Loading from 'component/Loading';
import { InventoryType } from 'features/warehouses/inventory/inventory';

/** CONSTANTS */
import { icons, onImgError } from 'constants/images';
import { colors } from 'constants/theme';

/** STYLE */
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CachedIcon from '@mui/icons-material/Cached';
import moment from 'moment';
import CheckboxButton from 'new-components/buttons/checkbox-button';
import MainButton from 'new-components/buttons/main-button';
import GeneralTab from './inventoryTab/general';
import HistoryAdjustmentTab from './inventoryTab/historyAdjustment';
import HistoryDispose from './inventoryTab/historyDispose';
import HistoryImportTab from './inventoryTab/historyImport';
import HistoryOrderTab from './inventoryTab/historyOrder';
import HistoryQuantity from './inventoryTab/historyQuantity';
import { CustomTabs } from './styled';

interface TabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
}

export interface InventoryTabs {
    inventory: InventoryType
}

export default function FormInventory() {
    const { t } = useTranslation()
    const location = useLocation()
    const history = useHistory()

    const tabs = ['GENERAL', 'IMPORT', 'HISTORY', 'ADJUSTMENT', 'QUANTITY', 'DISPOSE'];
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);

    const [inventory, setInventory] = useState<any>({});
    const [quantities, setQuantities] = useState<any>([]);
    const [totalExpire, setTotalExpire] = useState<number>(0)
    const [totalStillExpire, setTotalStillExpire] = useState<number>(0)

    const loadData = async (id: number) => {
        let itemId = 0
        await InventoryApi.findById(id)
            .then(({ data }) => {
                itemId = data?.itemId
                setInventory(data)
            })
            .catch(() => { return })

        if (!itemId) return setLoading(false)
        const condition: any = { itemId: itemId, page: 1, pageLimit: 100, sortBy: 'date', sortType: 'DESC' }
        await InventoryApi.findInventoryHistoryById(itemId, condition)
            .then(({ data }) => {
                setQuantities(data)

                const dateCurrent = moment();
                let newTotalExpire = 0
                let newTotalStillExpire = 0
                data?.forEach((item: any) => {
                    const dateInfo = moment(item?.expireDate);
                    if (item?.expireDate && dateCurrent.isAfter(dateInfo)) {
                        newTotalExpire += item?.qty
                    } else {
                        newTotalStillExpire += item?.qty
                    }
                })
                setTotalExpire(newTotalExpire)
                setTotalStillExpire(newTotalStillExpire)
            })
            .catch(() => { return })
            .finally(() => setLoading(false))

    }

    const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue)
    }

    const renderImage = (image: any, group: 'PRODUCT' | 'MEDICINE' | '') => {
        if (!_.isEmpty(image)) return `${fileUrl}/${image.filePath}/${image.fileName}`
        else return group === 'PRODUCT' ? icons.prodDefault : icons.medDefault
    }

    useEffect(() => {
        const inventoryId: any = location.state
        if (!_.isEmpty(inventoryId) && inventoryId.inventoryId) loadData(inventoryId.inventoryId)
        else history.push(routeName.inventory)
    }, [location])

    return (
        <Box height="100%" sx={{ overflow: 'auto' }}>
            <Box height="100%" display="flex" flexDirection="column">
                <HeaderLabel
                    unDisplayButton
                    text={t('INVENTORY.HEADER.TITLE')}
                    navigator={{ previousTo: routeName.inventory, previousTitle: t('INVENTORY.HEADER.DEFAULT'), currentTitle: t('INVENTORY.HEADER.VIEW') }}
                    inputStatus={
                        <Grid container justifyContent={'flex-end'}>
                            <Grid item mr={1}>
                                <MainButton
                                    onClick={() => history.push(routeName.importProduct + '/create',
                                        {
                                            itemId: inventory?.itemId,
                                            name: inventory?.itemName || inventory?.itemNameEn,
                                            salePrice: inventory?.price,
                                            group: inventory?.group,
                                            unit: inventory?.itemUnitName || inventory?.itemUnitNameEn
                                        })}
                                    title={t('INVENTORY.BUTTON.IMPORT')}
                                    type='primary'
                                    variant='outlined'
                                    startIcon={<AddCircleIcon />}
                                />
                            </Grid>
                            <Grid item>
                                <MainButton
                                    onClick={() => history.push(routeName.adjustment + '/create', { productName: inventory?.itemName, productId: inventory?.itemId })}
                                    title={t('INVENTORY.BUTTON.ADJUSTMENT')}
                                    type='primary'
                                    variant='outlined'
                                    startIcon={<CachedIcon />}
                                />
                            </Grid>
                        </Grid>
                    }
                />
                <Box border="1px solid" borderColor={colors.lightGray} boxShadow={1} mx={3} mb={3} height="100%" overflow="hidden" borderRadius={'8px'} bgcolor="#fff">
                    <Box height="100%" py={0} pl={2} pr={3} overflow="auto" display="flex" flexDirection="column" >
                        <Grid container spacing={2} alignItems="flex-start">
                            <Grid item xs={12} sm={4} md={2} xl={1.5}>
                                <Box sx={{ position: "relative", pt: "100%", border: `1px solid ${colors.lightGray}`, maxWidth: 200, maxHeight: 200, height: 200, mx: { xs: "auto", sm: 0 }, mt: 2 }}>
                                    <Box component="img" src={renderImage(inventory?.image, inventory?.group)} onError={onImgError} alt=""
                                        sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "contain" }} />
                                </Box>
                            </Grid>
                            {/* ข้อมูลสินค้า */}
                            <Grid item xs={12} sm={8} md={10} xl={10.5} >
                                <Box sx={{ display: "flex", flexDirection: "column", pt: { xs: 2, sm: 4 }, pl: { xs: 0, sm: 3, xl: 3 }, pb: { xs: 0, sm: 4 } }}  >
                                    <Typography sx={{ fontWeight: 500, fontSize: "20px", color: colors.textPrimary }} >
                                        {inventory?.itemName || inventory?.itemNameEn || "-"}
                                    </Typography>
                                    <Typography sx={{ pt: 2, color: colors.disabledGray }}>
                                        {`${t("INVENTORY.FORM.QTY")} ${totalStillExpire} ${inventory?.itemUnitName || inventory?.itemUnitNameEn || ""}`}
                                    </Typography>
                                    <Typography sx={{ pt: 2, color: colors.disabledGray }}>
                                        {`${t("INVENTORY.FORM.MEDICINE.EXPIRE_CURRENT")} ${totalExpire} ${inventory?.itemUnitName || inventory?.itemUnitNameEn || ""}`}
                                    </Typography>
                                    <Box sx={{ mt: { xs: 4, sm: "auto", xl: 1 }, xl: { transform: 'translateX(14px)' } }}>
                                        <CheckboxButton
                                            disabled
                                            checked={inventory.showOnReceipt === "SHOW"}
                                            onChange={(e) => setInventory({ ...inventory, showOnReceipt: e.target.value })}
                                            label={t("INVENTORY.FORM.SHOW_ON_RECEIPT")}

                                        />
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>

                        <Box className="mt-4">
                            <Box className='mb-3'>
                                <CustomTabs value={activeTab} onChange={handleChangeTab} aria-label="inventory tabs">
                                    {_.map(tabs, (tab: string, index: number) => {
                                        return <Tab key={index} label={t(`INVENTORY.TABS.${tab}`)} {...a11yProps(index)} />
                                    })}
                                </CustomTabs>
                            </Box>
                            <TabPanel value={activeTab} index={0}>
                                {!loading && <GeneralTab inventory={inventory} />}
                            </TabPanel>
                            <TabPanel value={activeTab} index={1}>
                                <HistoryImportTab inventory={inventory} />
                            </TabPanel>
                            <TabPanel value={activeTab} index={2}>
                                <HistoryOrderTab inventory={inventory} />
                            </TabPanel>
                            <TabPanel value={activeTab} index={3}>
                                <HistoryAdjustmentTab inventory={inventory} />
                            </TabPanel>
                            <TabPanel value={activeTab} index={4}>
                                <HistoryQuantity inventory={inventory} setTotalExpire={setTotalExpire} />
                            </TabPanel>
                            <TabPanel value={activeTab} index={5}>
                                <HistoryDispose inventory={inventory} />
                            </TabPanel>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Loading show={loading} type={'fullLoading'} />
        </Box>
    )
}

function a11yProps(index: number) {
    return {
        id: `inventory-tab-${index}`,
        'aria-controls': `inventory-tabpanel-${index}`,
    };
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`inventory-tabpanel-${index}`}
            aria-labelledby={`inventory-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </Box>
    );
}